import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import cogoToast from '@successtar/cogo-toast'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import OnScreenKeyboard from '@renderer/components/OnScreenKeyboard'

interface EmailGalleryModalProps {
  sessionId: string
  onClose: () => void
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Bahela email-gallery entry. Delegates to the shared full-size
 * OnScreenKeyboard (variant="email") so the kiosk keyboard opens and the
 * modal is proportionate to the page — same contract as the default/view
 * EmailGalleryModal. (Previously a hand-rolled small modal with a raw input
 * and no on-screen keyboard.)
 */
const EmailGalleryModal: React.FC<EmailGalleryModalProps> = ({ sessionId, onClose }) => {
  const { t, i18n } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | undefined>()
  const { setSessionState, logActivity } = useSessionStateStore()

  const submitEmail = useCallback(
    async (raw: string) => {
      const email = raw.trim()
      if (!email || !EMAIL_RE.test(email)) {
        setError(t('result.emailGallery.invalidEmail', 'Please enter a valid email address'))
        return
      }
      setError(undefined)
      setLoading(true)

      try {
        const locale = i18n.language || 'en'
        const response = await fetch(`${process.env.VITE_API_URL}/api/user-gallery`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            email,
            sessionId,
            share: true,
            locale,
            isOwnerShare: true
          })
        })

        const data = await response.json()

        if (response.ok) {
          setSessionState({
            userPreferences: {
              email
            }
          })
          logActivity('User sent gallery email', {
            email,
            sessionId,
            locale
          })
          cogoToast.success(t('result.emailGallery.shareSuccess'))
          onClose()
        } else {
          cogoToast.error(data.error || t('result.emailGallery.shareError'))
        }
      } catch (e) {
        console.error('Error sending email:', e)
        cogoToast.error(t('result.emailGallery.shareNetworkError'))
      } finally {
        setLoading(false)
      }
    },
    [i18n.language, logActivity, onClose, sessionId, setSessionState, t]
  )

  return (
    <OnScreenKeyboard
      variant="email"
      overlayClassName="!z-[100]"
      modalTitle={t('result.emailGallery.title')}
      placeholder={t('result.emailGallery.emailPlaceholder')}
      submitLabel={t('result.emailGallery.sendEmail')}
      submitLoadingLabel={t('result.emailGallery.sendingEmail')}
      error={error}
      isLoading={loading}
      onInputChange={() => setError(undefined)}
      onEnter={(value) => void submitEmail(value)}
      onClose={onClose}
    />
  )
}

export default EmailGalleryModal
